import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ShoppingCart, Heart, User, Menu, X, Search, ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { Button } from './ui/button';

export const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const { cartItemCount, wishlist } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === '/';

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-100">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          <div className="flex items-center gap-3">
            {!isHome && (
              <button onClick={() => navigate(-1)} className="text-[#0A192F] hover:text-[#D4AF37] transition-colors" data-testid="back-btn">
                <ArrowLeft size={22} />
              </button>
            )}
            <Link to="/" className="flex items-center" data-testid="nav-logo">
            <h1 className="text-2xl md:text-3xl font-bold text-[#0A192F]" style={{fontFamily: 'Playfair Display, serif'}}>
              ZINDAL
            </h1>
            <span className="ml-2 text-[#D4AF37] text-sm">&#10022;</span>
          </Link>
          </div>

          <div className="hidden md:flex items-center gap-8">
            <Link to="/products" className="text-[#0A192F] hover:text-[#D4AF37] transition-colors font-medium" data-testid="nav-products">
              Products
            </Link>
            <Link to="/bulk-inquiry" className="text-[#0A192F] hover:text-[#D4AF37] transition-colors font-medium" data-testid="nav-bulk">
              Bulk Orders
            </Link>
            <Link to="/track-order" className="text-[#0A192F] hover:text-[#D4AF37] transition-colors font-medium" data-testid="nav-track">
              Track Order
            </Link>
          </div>

          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/products')} className="hover:text-[#D4AF37] transition-colors hidden md:block" data-testid="nav-search-btn">
              <Search size={20} />
            </button>
            
            <Link to="/wishlist" className="relative hover:text-[#D4AF37] transition-colors" data-testid="nav-wishlist">
              <Heart size={20} />
              {wishlist.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-[#D4AF37] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {wishlist.length}
                </span>
              )}
            </Link>

            <Link to="/cart" className="relative hover:text-[#D4AF37] transition-colors" data-testid="nav-cart">
              <ShoppingCart size={20} />
              {cartItemCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-[#D4AF37] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {cartItemCount}
                </span>
              )}
            </Link>

            {user ? (
              <div className="relative group">
                <button className="flex items-center gap-2 hover:text-[#D4AF37] transition-colors" data-testid="nav-user-menu">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#c9a22e] flex items-center justify-center text-white text-xs font-bold" data-testid="nav-user-avatar">
                    {(user.name || 'U').charAt(0).toUpperCase()}
                  </div>
                  <span className="hidden md:block text-sm" data-testid="nav-user-name">
                    Hi, <b>{user.name}</b>
                  </span>
                </button>
                <div className="absolute right-0 top-full mt-2 bg-white border border-[#E2E8F0] shadow-lg w-56 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-bold truncate">{user.name}</p>
                    {user.email && <p className="text-xs text-gray-500 truncate">{user.email}</p>}
                    {user.isGuest && <p className="text-[10px] text-[#D4AF37] font-semibold">Guest session</p>}
                  </div>
                  <Link to="/orders" className="block px-4 py-3 hover:bg-gray-50 text-sm" data-testid="nav-orders">
                    My Orders
                  </Link>
                  <button onClick={handleLogout} className="w-full text-left px-4 py-3 hover:bg-gray-50 text-sm text-red-600" data-testid="nav-logout">
                    Logout
                  </button>
                </div>
              </div>
            ) : (
              <Button onClick={() => navigate('/login')} className="hidden md:flex bg-[#D4AF37] text-white hover:bg-[#c9a22e]" data-testid="nav-login-btn">
                Login
              </Button>
            )}

            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden" data-testid="nav-mobile-menu-btn">
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-100" data-testid="mobile-menu">
            <Link to="/products" onClick={() => setMobileMenuOpen(false)} className="block py-3 hover:text-[#D4AF37] transition-colors font-medium" data-testid="mobile-products">
              Products
            </Link>
            <Link to="/bulk-inquiry" onClick={() => setMobileMenuOpen(false)} className="block py-3 hover:text-[#D4AF37] transition-colors font-medium" data-testid="mobile-bulk">
              Bulk Orders
            </Link>
            <Link to="/track-order" onClick={() => setMobileMenuOpen(false)} className="block py-3 hover:text-[#D4AF37] transition-colors font-medium" data-testid="mobile-track">
              Track Order
            </Link>
            {!user ? (
              <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="block py-3 hover:text-[#D4AF37] transition-colors font-medium" data-testid="mobile-login">
                Login
              </Link>
            ) : (
              <>
                <Link to="/orders" onClick={() => setMobileMenuOpen(false)} className="block py-3 hover:text-[#D4AF37] transition-colors font-medium" data-testid="mobile-orders">
                  My Orders
                </Link>
                <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="block py-3 hover:text-[#D4AF37] transition-colors font-medium text-left w-full" data-testid="mobile-logout">
                  Logout
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};
